import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { subscriptionApi } from './api';

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  const [state, setState] = useState('verifying'); // verifying | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const hasVerified = useRef(false);

  useEffect(() => {
    if (hasVerified.current) return;
    hasVerified.current = true;

    if (!sessionId) {
      setState('success');
      return;
    }

    subscriptionApi.completeCheckout(sessionId)
      .then(() => setState('success'))
      .catch((err) => {
        setErrorMsg(err.message || 'Verification failed. Your subscription may still be active.');
        setState('error');
      });
  }, [sessionId]);

  useEffect(() => {
    if (state !== 'success') return;
    const timer = setTimeout(() => navigate('/dashboard', { replace: true }), 3000);
    return () => clearTimeout(timer);
  }, [state, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardContent className="p-8 flex flex-col items-center text-center gap-6">
          {state === 'verifying' && (
            <>
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Loader2 className="h-8 w-8 text-primary animate-spin" />
              </div>
              <div className="space-y-2">
                <h1 className="text-2xl font-semibold">Verifying Payment</h1>
                <p className="text-sm text-muted-foreground">
                  Please wait while we confirm your subscription...
                </p>
              </div>
            </>
          )}

          {state === 'success' && (
            <>
              <div className="h-16 w-16 rounded-full bg-green-500/10 flex items-center justify-center animate-in zoom-in-50 duration-300">
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
              <div className="space-y-2">
                <h1 className="text-2xl font-semibold">Payment Successful!</h1>
                <p className="text-sm text-muted-foreground">
                  Your subscription is now active. You will be redirected to the dashboard in a few seconds.
                </p>
              </div>
              <div className="w-full bg-muted rounded-full h-1 overflow-hidden">
                <div className="bg-green-500 h-full animate-[shrink_3s_linear_forwards]" style={{ width: '100%' }} />
              </div>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/dashboard">Go to Dashboard now</Link>
              </Button>
            </>
          )}

          {state === 'error' && (
            <>
              <div className="h-16 w-16 rounded-full bg-yellow-500/10 flex items-center justify-center">
                <AlertTriangle className="h-8 w-8 text-yellow-500" />
              </div>
              <div className="space-y-2">
                <h1 className="text-2xl font-semibold">Verification Issue</h1>
                <p className="text-sm text-muted-foreground">
                  {errorMsg}
                </p>
                <p className="text-xs text-muted-foreground">
                  If you were charged, your subscription will be activated shortly. Contact support if the issue persists.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full">
                <Button variant="outline" className="flex-1" asChild>
                  <Link to="/subscription">My Subscription</Link>
                </Button>
                <Button className="flex-1" asChild>
                  <Link to="/dashboard">Dashboard</Link>
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
